import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Save, Upload, X, Image as ImageIcon, Settings, MapPin, Phone, Mail, Clock, Image, Facebook, Instagram, Youtube, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const SiteSettingsManagement = () => {
  const [activeTab, setActiveTab] = useState('contact'); // 'contact', 'logos', 'hero', 'social', 'seo'
  const [contactInfos, setContactInfos] = useState([]);
  const [settings, setSettings] = useState({});
  const [heroImages, setHeroImages] = useState([]);
  const [pageHeroImages, setPageHeroImages] = useState({
    about: { image: '', title: '', subtitle: '' },
    contact: { image: '', title: '', subtitle: '' },
    admissions: { image: '', title: '', subtitle: '' }
  });
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isHeroModalOpen, setIsHeroModalOpen] = useState(false);
  const [isPageHeroModalOpen, setIsPageHeroModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingHeroIndex, setEditingHeroIndex] = useState(null);
  const [editingPageHero, setEditingPageHero] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    type: 'address',
    title: '',
    content: '',
    link: '',
  });
  const [heroFormData, setHeroFormData] = useState({
    image: '',
    title: '',
    subtitle: '',
  });
  const [socialMedia, setSocialMedia] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
  });
  const [socialMediaForm, setSocialMediaForm] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
  });
  const [seoSettings, setSeoSettings] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  });

  useEffect(() => {
    fetchContactInfos();
    fetchSettings();
  }, []);

  const fetchContactInfos = async () => {
    try {
      const response = await fetch('http://localhost/api/pages.php?type=contact', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'İletişim bilgileri yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setContactInfos(data);
    } catch (error) {
      console.error('Error fetching contact infos:', error);
      toast.error('İletişim bilgileri yüklenirken hata oluştu');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      setSettings(data);
      
      // Hero resimlerini parse et
      if (data.homepage_hero_images) {
        try {
          let parsed = data.homepage_hero_images;
          
          // String ise parse et
          if (typeof parsed === 'string') {
            parsed = parsed.trim();
            // HTML entities'yi decode et (&quot; -> ")
            const textarea = document.createElement('textarea');
            textarea.innerHTML = parsed;
            parsed = textarea.value;
            
            // Eğer string JSON formatında ise parse et
            if (parsed.startsWith('[') || parsed.startsWith('{')) {
              parsed = JSON.parse(parsed);
            } else {
              // Boş string veya geçersiz format
              console.warn('Invalid hero images format:', parsed);
              setHeroImages([]);
              return;
            }
          }
          
          // Eğer parsed bir string ise (çift encode edilmiş), tekrar parse et
          if (typeof parsed === 'string') {
            const textarea = document.createElement('textarea');
            textarea.innerHTML = parsed;
            parsed = JSON.parse(textarea.value);
          }
          
          const heroArray = Array.isArray(parsed) ? parsed : [];
          setHeroImages(heroArray);
          console.log('Hero images loaded:', heroArray);
        } catch (e) {
          console.error('Error parsing hero images:', e);
          console.error('Raw value:', data.homepage_hero_images);
          // Hatalı veriyi temizle - boş array olarak kaydet
          setHeroImages([]);
          // Veritabanındaki bozuk veriyi düzelt
          fetch('http://localhost/api/settings.php', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              setting_key: 'homepage_hero_images',
              setting_value: '[]',
              setting_type: 'json',
            }),
          }).catch(err => console.error('Error fixing hero images:', err));
        }
      } else {
        console.log('No homepage_hero_images in settings');
        setHeroImages([]);
      }

      // Sosyal medya linklerini parse et
      const socialMediaData = {
        facebook: data.social_facebook || '',
        twitter: data.social_twitter || '',
        instagram: data.social_instagram || '',
        youtube: data.social_youtube || '',
      };
      setSocialMedia(socialMediaData);
      setSocialMediaForm(socialMediaData);
      
      // SEO ayarlarını yükle
      const seoData = {
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        meta_keywords: data.meta_keywords || '',
      };
      setSeoSettings(seoData);

      // Sayfa hero resimlerini yükle
      const parsePageHero = (value) => {
        if (!value) return { image: '', title: '', subtitle: '' };
        try {
          if (typeof value === 'string') {
            return JSON.parse(value);
          }
          return value;
        } catch (e) {
          console.error('Error parsing page hero:', e);
          return { image: '', title: '', subtitle: '' };
        }
      };
      
      const pageHeroData = {
        about: parsePageHero(data.about_hero_image),
        contact: parsePageHero(data.contact_hero_image),
        admissions: parsePageHero(data.admissions_hero_image)
      };
      setPageHeroImages(pageHeroData);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const deleteOldImage = async (imageUrl) => {
    if (!imageUrl || !imageUrl.includes('/uploads/')) return;
    
    try {
      await fetch('http://localhost/api/delete-image.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url: imageUrl }),
      });
    } catch (error) {
      // Sessizce hata yut (resim silinmese bile devam et)
      console.error('Error deleting old image:', error);
    }
  };

  const handleImageUpload = async (file, settingKey) => {
    if (!file) return;

    const MAX_FILE_SIZE = 40 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Dosya boyutu 40MB\'dan büyük olamaz');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    // Eski resmi sakla (yeni resim yüklendikten sonra silmek için)
    const oldImageUrl = settings[settingKey];

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await fetch('http://localhost/api/upload.php', {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload,
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Görsel yüklenirken hata oluştu');
        return;
      }

      let imageUrl = data.url || data.image_url || '';
      if (!imageUrl && data.filename) {
        imageUrl = `/uploads/${data.filename}`;
      }

      if (imageUrl && !imageUrl.startsWith('http')) {
        if (imageUrl.startsWith('/uploads/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else {
          imageUrl = `http://localhost/uploads/${imageUrl}`;
        }
      }

      if (imageUrl) {
        // Yeni resim başarıyla yüklendi, eski resmi sil
        if (oldImageUrl && oldImageUrl !== imageUrl && oldImageUrl.includes('/uploads/')) {
          await deleteOldImage(oldImageUrl);
        }
        
        // Settings'i güncelle
        await updateSetting(settingKey, imageUrl);
        toast.success('Görsel başarıyla yüklendi');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenirken hata oluştu');
    }
  };

  const updateSetting = async (key, value) => {
    try {
      const response = await fetch('http://localhost/api/settings.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          setting_key: key,
          setting_value: value,
          setting_type: 'text',
        }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', text);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Ayar güncellenirken hata oluştu');
        return;
      }

      // State'i güncelle ve settings'i tekrar çek
      setSettings(prev => ({ ...prev, [key]: value }));
      // Ayarları tekrar çekerek veritabanından doğrulama yap
      await fetchSettings();
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Ayar güncellenirken hata oluştu');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingContact
        ? `http://localhost/api/pages.php?type=contact&id=${editingContact.id}`
        : 'http://localhost/api/pages.php?type=contact';
      
      const method = editingContact ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...contactFormData,
          order_index: contactInfos.length,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'İletişim bilgisi kaydedilirken hata oluştu');
        return;
      }

      toast.success(editingContact ? 'İletişim bilgisi güncellendi' : 'İletişim bilgisi eklendi');
      setIsContactModalOpen(false);
      setEditingContact(null);
      setContactFormData({ type: 'address', title: '', content: '', link: '' });
      fetchContactInfos();
    } catch (error) {
      console.error('Error saving contact info:', error);
      toast.error('İletişim bilgisi kaydedilirken hata oluştu');
    }
  };

  const handleHeroSubmit = async (e) => {
    e.preventDefault();

    if (!heroFormData.image || !heroFormData.title || !heroFormData.subtitle) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      console.log('Saving hero form data:', heroFormData);
      const newHeroImages = [...heroImages];
      
      // Eğer düzenleme modundaysa ve resim değiştiyse, eski resmi sil
      if (editingHeroIndex !== null) {
        const oldHeroImage = heroImages[editingHeroIndex];
        if (oldHeroImage && oldHeroImage.image !== heroFormData.image && oldHeroImage.image && oldHeroImage.image.includes('/uploads/')) {
          await deleteOldImage(oldHeroImage.image);
        }
      }
      
      if (editingHeroIndex !== null) {
        newHeroImages[editingHeroIndex] = { ...heroFormData };
      } else {
        newHeroImages.push({ ...heroFormData });
      }
      
      console.log('New hero images array:', newHeroImages);

      // Veritabanına kaydet
      const jsonValue = JSON.stringify(newHeroImages);
      console.log('Saving to DB:', jsonValue);
      
      const response = await fetch('http://localhost/api/settings.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          setting_key: 'homepage_hero_images',
          setting_value: jsonValue,
          setting_type: 'json',
        }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Hero resmi kaydedilirken hata oluştu');
        return;
      }

      // State'i güncelle ve settings'i tekrar çek
      setHeroImages(newHeroImages);
      setSettings(prev => ({ ...prev, homepage_hero_images: JSON.stringify(newHeroImages) }));
      await fetchSettings(); // Veritabanından tekrar çekerek doğrulama yap
      
      setIsHeroModalOpen(false);
      setEditingHeroIndex(null);
      setHeroFormData({ image: '', title: '', subtitle: '' });
      toast.success('Hero resmi kaydedildi');
    } catch (error) {
      console.error('Error saving hero image:', error);
      toast.error('Hero resmi kaydedilirken hata oluştu');
    }
  };

  const deleteHeroImage = async (index) => {
    try {
      const heroImageToDelete = heroImages[index];
      
      // Eğer silinecek hero resminin image URL'i varsa, sunucudan da sil
      if (heroImageToDelete && heroImageToDelete.image && heroImageToDelete.image.includes('/uploads/')) {
        await deleteOldImage(heroImageToDelete.image);
      }
      
      const newHeroImages = heroImages.filter((_, i) => i !== index);
      
      // Veritabanına kaydet
      const response = await fetch('http://localhost/api/settings.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          setting_key: 'homepage_hero_images',
          setting_value: JSON.stringify(newHeroImages),
          setting_type: 'json',
        }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Hero resmi silinirken hata oluştu');
        return;
      }

      // State'i güncelle ve settings'i tekrar çek
      setHeroImages(newHeroImages);
      setSettings(prev => ({ ...prev, homepage_hero_images: JSON.stringify(newHeroImages) }));
      await fetchSettings(); // Veritabanından tekrar çekerek doğrulama yap
      
      toast.success('Hero resmi silindi');
    } catch (error) {
      console.error('Error deleting hero image:', error);
      toast.error('Hero resmi silinirken hata oluştu');
    }
  };

  const handlePageHeroSubmit = async (pageKey) => {
    try {
      const pageHero = pageHeroImages[pageKey];
      if (!pageHero.image) {
        toast.error('Lütfen bir resim yükleyin');
        return;
      }

      const settingKey = `${pageKey}_hero_image`;
      const response = await fetch('http://localhost/api/settings.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          setting_key: settingKey,
          setting_value: JSON.stringify(pageHero),
          setting_type: 'json',
        }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Sayfa hero resmi kaydedilirken hata oluştu');
        return;
      }

      setSettings(prev => ({ ...prev, [settingKey]: JSON.stringify(pageHero) }));
      setIsPageHeroModalOpen(false);
      setEditingPageHero(null);
      toast.success('Sayfa hero resmi kaydedildi');
    } catch (error) {
      console.error('Error saving page hero image:', error);
      toast.error('Sayfa hero resmi kaydedilirken hata oluştu');
    }
  };

  const handlePageHeroImageUpload = async (pageKey, file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('http://localhost/api/upload.php', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok || !data.success) {
        toast.error(data.message || 'Resim yüklenirken hata oluştu');
        return;
      }

      const imageUrl = data.url.startsWith('http') ? data.url : `http://localhost${data.url}`;
      
      // Eski resmi sil
      const oldImage = pageHeroImages[pageKey]?.image;
      if (oldImage && oldImage.includes('/uploads/')) {
        await deleteOldImage(oldImage);
      }

      setPageHeroImages(prev => ({
        ...prev,
        [pageKey]: {
          ...prev[pageKey],
          image: imageUrl
        }
      }));

      toast.success('Resim yüklendi');
    } catch (error) {
      console.error('Error uploading page hero image:', error);
      toast.error('Resim yüklenirken hata oluştu');
    }
  };

  const getIcon = (type) => {
    const icons = {
      address: <MapPin size={20} />,
      phone: <Phone size={20} />,
      email: <Mail size={20} />,
      hours: <Clock size={20} />,
    };
    return icons[type] || <MapPin size={20} />;
  };

  const handleSeoSubmit = async (e) => {
    e.preventDefault();
    
    if (!seoSettings.meta_title || !seoSettings.meta_description) {
      toast.error('Meta başlık ve açıklama zorunludur');
      return;
    }

    try {
      // Tüm SEO ayarlarını tek seferde güncelle
      const updates = [
        { key: 'meta_title', value: seoSettings.meta_title },
        { key: 'meta_description', value: seoSettings.meta_description },
        { key: 'meta_keywords', value: seoSettings.meta_keywords },
      ];

      const updatePromises = updates.map(({ key, value }) => {
        return fetch('http://localhost/api/settings.php', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            setting_key: key,
            setting_value: value || '',
            setting_type: 'text',
          }),
        });
      });

      const responses = await Promise.all(updatePromises);
      
      // Tüm yanıtları kontrol et
      for (const response of responses) {
        if (!response.ok) {
          const text = await response.text();
          let data = {};
          try {
            data = text ? JSON.parse(text) : {};
          } catch (e) {
            console.error('Error parsing response:', text);
          }
          throw new Error(data.error || 'SEO ayarları güncellenirken hata oluştu');
        }
      }
      
      // Ayarları tekrar çek
      await fetchSettings();
      toast.success('SEO ayarları başarıyla kaydedildi');
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      toast.error(error.message || 'SEO ayarları kaydedilirken hata oluştu');
    }
  };

  const handleSocialMediaSubmit = async (e) => {
    e.preventDefault();
    try {
      // Tüm sosyal medya ayarlarını tek seferde güncelle
      const updates = [
        { key: 'social_facebook', value: socialMediaForm.facebook },
        { key: 'social_twitter', value: socialMediaForm.twitter },
        { key: 'social_instagram', value: socialMediaForm.instagram },
        { key: 'social_youtube', value: socialMediaForm.youtube },
      ];

      const updatePromises = updates.map(({ key, value }) => {
        return fetch('http://localhost/api/settings.php', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            setting_key: key,
            setting_value: value,
            setting_type: 'text',
          }),
        });
      });

      const responses = await Promise.all(updatePromises);
      
      // Tüm yanıtları kontrol et
      for (const response of responses) {
        if (!response.ok) {
          const text = await response.text();
          let data = {};
          try {
            data = text ? JSON.parse(text) : {};
          } catch (e) {
            console.error('Error parsing response:', text);
          }
          throw new Error(data.error || 'Ayar güncellenirken hata oluştu');
        }
      }
      
      // Ayarları tekrar çek
      await fetchSettings();
      toast.success('Sosyal medya linkleri kaydedildi');
    } catch (error) {
      console.error('Error saving social media:', error);
      toast.error(error.message || 'Sosyal medya linkleri kaydedilirken hata oluştu');
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="mt-2 sm:mt-6">
          {/* Tabs */}
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-0 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('contact')}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
                activeTab === 'contact'
                  ? 'text-red-900 border-red-900'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              İletişim Bilgileri
            </button>
            <button
              onClick={() => setActiveTab('logos')}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
                activeTab === 'logos'
                  ? 'text-red-900 border-red-900'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Logolar & Favicon
            </button>
            <button
              onClick={() => setActiveTab('hero')}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
                activeTab === 'hero'
                  ? 'text-red-900 border-red-900'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Hero Resimleri
            </button>
            <button
              onClick={() => setActiveTab('social')}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
                activeTab === 'social'
                  ? 'text-red-900 border-red-900'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Sosyal Medya
            </button>
            <button
              onClick={() => setActiveTab('seo')}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
                activeTab === 'seo'
                  ? 'text-red-900 border-red-900'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              SEO Ayarları
            </button>
          </div>

          <div className="mt-6 sm:mt-4">
            {/* İletişim Bilgileri Sekmesi */}
            {activeTab === 'contact' && (
            <div className="space-y-8 mt-0">
          {/* İletişim Bilgileri */}
          <section className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Footer İletişim Bilgileri</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {contactInfos
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((info) => (
                  <div
                    key={info.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all flex flex-col"
                  >
                    <div className="flex items-start gap-3 mb-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-red-900 flex items-center justify-center text-white flex-shrink-0">
                        {getIcon(info.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm mb-1">{info.title}</h3>
                        <p className="text-xs text-gray-600 break-words leading-relaxed">{info.content}</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        setEditingContact(info);
                        setContactFormData({
                          type: info.type,
                          title: info.title,
                          content: info.content,
                          link: info.link || '',
                        });
                        setIsContactModalOpen(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full mt-auto"
                    >
                      Düzenle
                    </Button>
                  </div>
                ))}
            </div>
          </section>
            </div>
            )}

            {/* Logolar & Favicon Sekmesi */}
            {activeTab === 'logos' && (
            <div className="space-y-8 mt-0">
              {/* Logolar */}
              <section className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Logolar</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Kolej Logosu */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Kolej Logosu</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-900 hover:bg-red-50/50 transition-all cursor-pointer"
                        onClick={() => {
                          const input = document.getElementById('kolej-logo-upload');
                          input?.click();
                        }}
                      >
                        <input
                          id="kolej-logo-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, 'kolej_logo');
                          }}
                        />
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Tıklayarak yükle</p>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center bg-gray-50 min-h-[120px]">
                        {settings.kolej_logo ? (
                          <img src={settings.kolej_logo} alt="Kolej Logo" className="max-w-full max-h-24 object-contain" />
                        ) : (
                          <ImageIcon className="w-12 h-12 text-gray-300" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Kurs Merkezi Logosu */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Kurs Merkezi Logosu</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-900 hover:bg-red-50/50 transition-all cursor-pointer"
                        onClick={() => {
                          const input = document.getElementById('kurs-logo-upload');
                          input?.click();
                        }}
                      >
                        <input
                          id="kurs-logo-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, 'kurs_merkezi_logo');
                          }}
                        />
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Tıklayarak yükle</p>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center bg-gray-50 min-h-[120px]">
                        {settings.kurs_merkezi_logo ? (
                          <img src={settings.kurs_merkezi_logo} alt="Kurs Merkezi Logo" className="max-w-full max-h-24 object-contain" />
                        ) : (
                          <ImageIcon className="w-12 h-12 text-gray-300" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Faviconlar */}
              <section className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Faviconlar</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Kolej Favicon */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Kolej Favicon</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-900 hover:bg-red-50/50 transition-all cursor-pointer"
                        onClick={() => {
                          const input = document.getElementById('kolej-favicon-upload');
                          input?.click();
                        }}
                      >
                        <input
                          id="kolej-favicon-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, 'kolej_favicon');
                          }}
                        />
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Tıklayarak yükle</p>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center bg-gray-50 min-h-[80px]">
                        {settings.kolej_favicon ? (
                          <img src={settings.kolej_favicon} alt="Kolej Favicon" className="w-16 h-16 object-contain" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-gray-300" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Kurs Merkezi Favicon */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Kurs Merkezi Favicon</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-900 hover:bg-red-50/50 transition-all cursor-pointer"
                        onClick={() => {
                          const input = document.getElementById('kurs-favicon-upload');
                          input?.click();
                        }}
                      >
                        <input
                          id="kurs-favicon-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, 'kurs_merkezi_favicon');
                          }}
                        />
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Tıklayarak yükle</p>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center bg-gray-50 min-h-[80px]">
                        {settings.kurs_merkezi_favicon ? (
                          <img src={settings.kurs_merkezi_favicon} alt="Kurs Merkezi Favicon" className="w-16 h-16 object-contain" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-gray-300" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            )}

            {/* Hero Resimleri Sekmesi */}
            {activeTab === 'hero' && (
            <div className="space-y-8 mt-0">
              {/* Ana Sayfa Hero Resimleri */}
              <section className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Ana Sayfa Hero Resimleri</h2>
                  <Button
                    onClick={() => {
                      setEditingHeroIndex(null);
                      setHeroFormData({ image: '', title: '', subtitle: '' });
                      setIsHeroModalOpen(true);
                    }}
                    className="bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700"
                  >
                    <Upload size={16} className="mr-2" />
                    Yeni Hero Resmi Ekle
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {heroImages.map((hero, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
                      {hero.image ? (
                        <img 
                          src={hero.image} 
                          alt={hero.title || 'Hero'} 
                          className="w-full h-32 object-cover"
                          onError={(e) => {
                            console.error('Image load error:', hero.image);
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-32 bg-gray-200 flex items-center justify-center"><span class="text-gray-400 text-sm">Görsel yüklenemedi</span></div>';
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400 text-sm">Görsel yok</span>
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 text-sm mb-1 truncate">{hero.title || 'Başlıksız'}</h3>
                        <p className="text-xs text-gray-600 truncate">{hero.subtitle || 'Alt başlık yok'}</p>
                        <div className="flex gap-2 mt-3">
                          <Button
                            onClick={() => {
                              setEditingHeroIndex(index);
                              setHeroFormData({ ...hero });
                              setIsHeroModalOpen(true);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex-1"
                          >
                            Düzenle
                          </Button>
                          <Button
                            onClick={() => deleteHeroImage(index)}
                            variant="destructive"
                            size="sm"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {heroImages.length === 0 && (
                    <div className="col-span-full text-center py-8 text-gray-500">
                      Henüz hero resmi eklenmemiş
                    </div>
                  )}
                </div>
              </section>

              {/* Sayfa Hero Resimleri */}
              <section className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Sayfa Hero Resimleri</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Hakkımızda Hero */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="font-semibold text-gray-900">Hakkımızda Sayfası</h3>
                    </div>
                    <div className="p-4">
                      {pageHeroImages.about?.image ? (
                        <img 
                          src={pageHeroImages.about.image} 
                          alt="Hakkımızda Hero" 
                          className="w-full h-32 object-cover rounded mb-4"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 flex items-center justify-center rounded mb-4">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="space-y-2 mb-4">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Başlık:</span> {pageHeroImages.about?.title || 'Belirtilmemiş'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Alt Başlık:</span> {pageHeroImages.about?.subtitle || 'Belirtilmemiş'}
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          setEditingPageHero('about');
                          setIsPageHeroModalOpen(true);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        {pageHeroImages.about?.image ? 'Düzenle' : 'Resim Ekle'}
                      </Button>
                    </div>
                  </div>

                  {/* İletişim Hero */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="font-semibold text-gray-900">İletişim Sayfası</h3>
                    </div>
                    <div className="p-4">
                      {pageHeroImages.contact?.image ? (
                        <img 
                          src={pageHeroImages.contact.image} 
                          alt="İletişim Hero" 
                          className="w-full h-32 object-cover rounded mb-4"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 flex items-center justify-center rounded mb-4">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="space-y-2 mb-4">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Başlık:</span> {pageHeroImages.contact?.title || 'Belirtilmemiş'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Alt Başlık:</span> {pageHeroImages.contact?.subtitle || 'Belirtilmemiş'}
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          setEditingPageHero('contact');
                          setIsPageHeroModalOpen(true);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        {pageHeroImages.contact?.image ? 'Düzenle' : 'Resim Ekle'}
                      </Button>
                    </div>
                  </div>

                  {/* Kabul Hero */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b">
                      <h3 className="font-semibold text-gray-900">Kabul Sayfası</h3>
                    </div>
                    <div className="p-4">
                      {pageHeroImages.admissions?.image ? (
                        <img 
                          src={pageHeroImages.admissions.image} 
                          alt="Kabul Hero" 
                          className="w-full h-32 object-cover rounded mb-4"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 flex items-center justify-center rounded mb-4">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="space-y-2 mb-4">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Başlık:</span> {pageHeroImages.admissions?.title || 'Belirtilmemiş'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Alt Başlık:</span> {pageHeroImages.admissions?.subtitle || 'Belirtilmemiş'}
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          setEditingPageHero('admissions');
                          setIsPageHeroModalOpen(true);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        {pageHeroImages.admissions?.image ? 'Düzenle' : 'Resim Ekle'}
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            )}

            {/* Sosyal Medya Sekmesi */}
            {activeTab === 'social' && (
            <div className="space-y-8 mt-0">
              <section className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Sosyal Medya Linkleri</h2>
                </div>
                <form onSubmit={handleSocialMediaSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Facebook */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Facebook className="w-5 h-5 text-blue-600" />
                        Facebook
                      </label>
                      <Input
                        type="url"
                        value={socialMediaForm.facebook}
                        onChange={(e) => {
                          setSocialMediaForm(prev => ({ ...prev, facebook: e.target.value }));
                        }}
                        placeholder="https://facebook.com/..."
                        className="w-full"
                      />
                    </div>

                    {/* X (Twitter) */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        X (Twitter)
                      </label>
                      <Input
                        type="url"
                        value={socialMediaForm.twitter}
                        onChange={(e) => {
                          setSocialMediaForm(prev => ({ ...prev, twitter: e.target.value }));
                        }}
                        placeholder="https://x.com/..."
                        className="w-full"
                      />
                    </div>

                    {/* Instagram */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Instagram className="w-5 h-5 text-pink-600" />
                        Instagram
                      </label>
                      <Input
                        type="url"
                        value={socialMediaForm.instagram}
                        onChange={(e) => {
                          setSocialMediaForm(prev => ({ ...prev, instagram: e.target.value }));
                        }}
                        placeholder="https://instagram.com/..."
                        className="w-full"
                      />
                    </div>

                    {/* YouTube */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Youtube className="w-5 h-5 text-red-600" />
                        YouTube
                      </label>
                      <Input
                        type="url"
                        value={socialMediaForm.youtube}
                        onChange={(e) => {
                          setSocialMediaForm(prev => ({ ...prev, youtube: e.target.value }));
                        }}
                        placeholder="https://youtube.com/..."
                        className="w-full"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      type="submit"
                      className="bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700"
                    >
                      <Save size={16} className="mr-2" />
                      Kaydet
                    </Button>
                  </div>
                </form>
              </section>
            </div>
            )}

            {/* SEO Ayarları */}
            {activeTab === 'seo' && (
            <div className="space-y-8 mt-0">
              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Search className="w-6 h-6 text-[#C0A062]" />
                  <h3 className="text-2xl font-bold text-gray-900">SEO Ayarları</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Arama motorları için site meta bilgilerini yönetin. Bu bilgiler Google ve diğer arama motorlarında görünecektir.
                </p>

                <form onSubmit={handleSeoSubmit} className="space-y-6">
                  {/* Meta Tags */}
                  <div className="border-b pb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Meta Etiketleri</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Meta Başlık (Title) *
                        </label>
                        <Input
                          type="text"
                          value={seoSettings.meta_title}
                          onChange={(e) => setSeoSettings({...seoSettings, meta_title: e.target.value})}
                          placeholder="Serpil Şahin Koleji - Geleceğin Yolculuğu Burada Başlıyor"
                          maxLength={60}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {seoSettings.meta_title.length}/60 karakter (Önerilen: 50-60 karakter)
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Meta Açıklama (Description) *
                        </label>
                        <Textarea
                          value={seoSettings.meta_description}
                          onChange={(e) => setSeoSettings({...seoSettings, meta_description: e.target.value})}
                          placeholder="Malatya'nın önde gelen eğitim kurumlarından Serpil Şahin Koleji, ilkokul, ortaokul ve lise eğitimi sunuyor."
                          rows={3}
                          maxLength={160}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {seoSettings.meta_description.length}/160 karakter (Önerilen: 120-160 karakter)
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Meta Anahtar Kelimeler (Keywords)
                        </label>
                        <Input
                          type="text"
                          value={seoSettings.meta_keywords}
                          onChange={(e) => setSeoSettings({...seoSettings, meta_keywords: e.target.value})}
                          placeholder="kolej, eğitim, okul, malatya, ortaokul"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Virgülle ayrılmış anahtar kelimeler
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="bg-[#C0A062] hover:bg-[#9a7d4a] text-white font-semibold px-8 py-3 rounded-lg"
                  >
                    <Save size={20} className="mr-2" />
                    SEO Ayarlarını Kaydet
                  </Button>
                </form>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* İletişim Bilgisi Modal */}
      <Dialog open={isContactModalOpen} onOpenChange={setIsContactModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>İletişim Bilgisini Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık</label>
              <Input
                value={contactFormData.title}
                onChange={(e) => setContactFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">İçerik</label>
              <Input
                value={contactFormData.content}
                onChange={(e) => setContactFormData(prev => ({ ...prev, content: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Link (Opsiyonel)</label>
              <Input
                value={contactFormData.link}
                onChange={(e) => setContactFormData(prev => ({ ...prev, link: e.target.value }))}
                placeholder="tel:..., mailto:..."
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-red-900 to-red-800">
                <Save size={16} className="mr-2" />
                Kaydet
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsContactModalOpen(false)}>
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hero Resmi Modal */}
      <Dialog open={isHeroModalOpen} onOpenChange={setIsHeroModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingHeroIndex !== null ? 'Hero Resmini Düzenle' : 'Yeni Hero Resmi Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleHeroSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Resim</label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-900 hover:bg-red-50/50 transition-all cursor-pointer"
                  onClick={() => {
                    const input = document.getElementById('hero-image-upload');
                    input?.click();
                  }}
                >
                  <input
                    id="hero-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const MAX_FILE_SIZE = 40 * 1024 * 1024;
                        if (file.size > MAX_FILE_SIZE) {
                          toast.error('Dosya boyutu 40MB\'dan büyük olamaz');
                          return;
                        }
                        
                        // Eski resmi sakla (yeni resim yüklendikten sonra silmek için)
                        const oldImageUrl = heroFormData.image;
                        
                        try {
                          const formDataUpload = new FormData();
                          formDataUpload.append('image', file);
                          const response = await fetch('http://localhost/api/upload.php', {
                            method: 'POST',
                            credentials: 'include',
                            body: formDataUpload,
                          });
                          
                          const text = await response.text();
                          let data = {};
                          try {
                            data = text ? JSON.parse(text) : {};
                          } catch (parseError) {
                            console.error('Error parsing upload response:', parseError, text);
                            toast.error('Sunucu yanıtı işlenemedi');
                            return;
                          }
                          
                          if (!response.ok) {
                            toast.error(data.error || data.message || 'Görsel yüklenirken hata oluştu');
                            return;
                          }
                          
                          let imageUrl = data.url || data.image_url || '';
                          if (!imageUrl && data.filename) {
                            imageUrl = `/uploads/${data.filename}`;
                          }
                          if (imageUrl && !imageUrl.startsWith('http')) {
                            imageUrl = imageUrl.startsWith('/uploads/') 
                              ? `http://localhost${imageUrl}` 
                              : `http://localhost/uploads/${imageUrl}`;
                          }
                          
                          if (imageUrl) {
                            // Yeni resim başarıyla yüklendi, eski resmi sil
                            if (oldImageUrl && oldImageUrl !== imageUrl && oldImageUrl.includes('/uploads/')) {
                              await deleteOldImage(oldImageUrl);
                            }
                            
                            setHeroFormData(prev => ({ ...prev, image: imageUrl }));
                            toast.success('Görsel yüklendi');
                          } else {
                            toast.error('Görsel URL\'si alınamadı');
                            console.error('Upload response data:', data);
                          }
                        } catch (error) {
                          console.error('Error uploading hero image:', error);
                          toast.error('Görsel yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
                        }
                      }
                    }}
                  />
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Tıklayarak yükle</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center bg-gray-50 min-h-[120px]">
                  {heroFormData.image ? (
                    <img src={heroFormData.image} alt="Preview" className="max-w-full max-h-24 object-contain" />
                  ) : (
                    <ImageIcon className="w-12 h-12 text-gray-300" />
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık</label>
              <Input
                value={heroFormData.title}
                onChange={(e) => setHeroFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Alt Başlık</label>
              <Input
                value={heroFormData.subtitle}
                onChange={(e) => setHeroFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                required
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-red-900 to-red-800">
                <Save size={16} className="mr-2" />
                Kaydet
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsHeroModalOpen(false)}>
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sayfa Hero Resmi Modal */}
      <Dialog open={isPageHeroModalOpen} onOpenChange={setIsPageHeroModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPageHero === 'about' && 'Hakkımızda Sayfası Hero Resmi'}
              {editingPageHero === 'contact' && 'İletişim Sayfası Hero Resmi'}
              {editingPageHero === 'admissions' && 'Kabul Sayfası Hero Resmi'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (editingPageHero) {
              handlePageHeroSubmit(editingPageHero);
            }
          }} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Resim</label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-red-500 transition-colors flex flex-col items-center justify-center text-center"
                  onClick={() => {
                    const input = document.getElementById('page-hero-image-upload');
                    input?.click();
                  }}
                >
                  <input
                    id="page-hero-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file && editingPageHero) {
                        const MAX_FILE_SIZE = 40 * 1024 * 1024;
                        if (file.size > MAX_FILE_SIZE) {
                          toast.error('Dosya boyutu 40MB\'dan büyük olamaz');
                          return;
                        }
                        await handlePageHeroImageUpload(editingPageHero, file);
                      }
                    }}
                  />
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-xs text-gray-500">Tıklayarak yükle</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center bg-gray-50 min-h-[120px]">
                  {editingPageHero && pageHeroImages[editingPageHero]?.image ? (
                    <img src={pageHeroImages[editingPageHero].image} alt="Preview" className="max-w-full max-h-24 object-contain" />
                  ) : (
                    <ImageIcon className="w-12 h-12 text-gray-300" />
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık</label>
              <Input
                value={editingPageHero ? (pageHeroImages[editingPageHero]?.title || '') : ''}
                onChange={(e) => {
                  if (editingPageHero) {
                    setPageHeroImages(prev => ({
                      ...prev,
                      [editingPageHero]: {
                        ...prev[editingPageHero],
                        title: e.target.value
                      }
                    }));
                  }
                }}
                placeholder="Örn: Hakkımızda"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Alt Başlık</label>
              <Input
                value={editingPageHero ? (pageHeroImages[editingPageHero]?.subtitle || '') : ''}
                onChange={(e) => {
                  if (editingPageHero) {
                    setPageHeroImages(prev => ({
                      ...prev,
                      [editingPageHero]: {
                        ...prev[editingPageHero],
                        subtitle: e.target.value
                      }
                    }));
                  }
                }}
                placeholder="Örn: 25 yılı aşkın deneyimimizle..."
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-red-900 to-red-800">
                <Save size={16} className="mr-2" />
                Kaydet
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setIsPageHeroModalOpen(false);
                setEditingPageHero(null);
              }}>
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default SiteSettingsManagement;

